import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import {
  CreateShipmentPrefillSettingsDTO,
  CreateShipmentPrefillSnDTO,
  ExpeditionModuleSettingsDTO,
  CompanyFolderOutDTO,
  EntityTypeEnum,
  FolderSettingsDTO,
} from '@api/ringil-api.v2.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { isNullOrUndefined } from '@utils/utils.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';

interface ComponentProps {
  companyId: string;
}

const ModuleSettingsExpedition: React.FC<ComponentProps> = ({ companyId }) => {
  const { getExpeditionModuleSettings, upsertExpeditionModuleSettings, listCompanyFolder } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);
  const [settings, setSettings] = useState<ExpeditionModuleSettingsDTO>();
  const [folders, setFolders] = useState<CompanyFolderOutDTO[]>([]);

  useEffect(() => {
    getExpeditionModuleSettings(companyId, d => setSettings(d));
    // RD-2977: expedition folders of the company for the default import folder dropdown
    listCompanyFolder(companyId, EntityTypeEnum.EXPEDITION, d => setFolders(d?.folders ?? []));
  }, []);

  if (isNullOrUndefined(settings)) return <></>;

  const prefill = settings.createShipmentPrefill;
  const routeOn = prefill.routeAndTransport;
  const folderSettings: FolderSettingsDTO = settings.folders ?? { required: false, restrictVisibility: false, defaultImportFolderId: undefined };

  // every setter spreads the whole DTO so adding a block never drops another one
  const setPrefill = (patch: Partial<CreateShipmentPrefillSettingsDTO>) =>
    setSettings(s => ({ ...s, createShipmentPrefill: { ...s.createShipmentPrefill, ...patch } }));
  const setSn = (patch: Partial<CreateShipmentPrefillSnDTO>) =>
    setSettings(s => ({ ...s, createShipmentPrefill: { ...s.createShipmentPrefill, sn: { ...s.createShipmentPrefill.sn, ...patch } } }));
  const setFolders2 = (patch: Partial<FolderSettingsDTO>) =>
    setSettings(s => ({ ...s, folders: { ...(s.folders ?? { required: false, restrictVisibility: false }), ...patch } }));

  // route is the master gate for every other field — flip them all with it, both directions
  const setRoute = (v: boolean) => {
    setPrefill({
      routeAndTransport: v,
      cargoAndOrders: v,
      labels: v,
      internalInfo: v,
      documents: v,
      carrierChoice: v,
      carrierNote: v,
    });
  };

  const save = () => {
    upsertExpeditionModuleSettings(companyId, settings, () => {
      showToastMessage('Saved', 'Expedition settings saved', 'success');
    });
  };

  return (
    <div>
      <BreadCrumbComponent items={[{ label: 'Expedition module settings' }]} />
      <COHeadline title={tr('ModuleSettingsExpedition.expeditionModuleSettings', 'Expedition module settings')} variant={'h2'} />
      <div className={'flex flex-col gap-4 w-[500px]'}>
        <COHeadline title={tr('ModuleSettingsExpedition.createShipmentPrefill', 'Create shipment prefill')} variant={'h3'} />
        <RingilInputSwitch
          value={prefill.routeAndTransport}
          onChange={setRoute}
          label={tr('PrefillFromFavoritesSidebar.routeAndParameters', 'Route and transport parameters')}
        />
        <RingilInputSwitch
          value={prefill.cargoAndOrders}
          disabled={!routeOn}
          onChange={v => setPrefill({ cargoAndOrders: v })}
          label={tr('PrefillFromFavoritesSidebar.cargoAndOrders', 'Cargo and orders')}
        />
        <RingilInputSwitch
          value={prefill.labels}
          disabled={!routeOn}
          onChange={v => setPrefill({ labels: v })}
          label={tr('PrefillFromFavoritesSidebar.labels', 'Labels')}
        />
        <RingilInputSwitch
          value={prefill.internalInfo}
          disabled={!routeOn}
          onChange={v => setPrefill({ internalInfo: v })}
          label={tr('PrefillFromFavoritesSidebar.internalInfo', 'Internal info')}
        />
        <RingilInputSwitch
          value={prefill.documents}
          disabled={!routeOn}
          onChange={v => setPrefill({ documents: v })}
          label={tr('PrefillFromFavoritesSidebar.documents', 'Documents')}
        />
        <RingilInputSwitch
          value={prefill.carrierChoice}
          disabled={!routeOn}
          onChange={v => setPrefill({ carrierChoice: v })}
          label={tr('PrefillFromFavoritesSidebar.carrierSelection', 'Carrier selection')}
        />
        <RingilInputSwitch
          value={prefill.carrierNote}
          disabled={!routeOn}
          onChange={v => setPrefill({ carrierNote: v })}
          label={tr('CarrierNoteTag.title', 'Note for carrier')}
        />

        <HorizontalLine variant={'tertiary'} />
        <COHeadline title={tr('ModuleSettingsExpedition.supplierNotification', 'Supplier notification')} variant={'h3'} />
        <RingilInputSwitch
          value={prefill.sn.linkToSn}
          onChange={v => setSn({ linkToSn: v })}
          label={tr('DuplicateExpeditionDialog.linkovatNaSn', 'Linkovat na SN')}
        />
        <RingilInputSwitch
          value={prefill.sn.copySnAttachments}
          onChange={v => setSn(v ? { copySnAttachments: true } : { copySnAttachments: false, markAsPrivate: false })}
          label={tr('DuplicateExpeditionDialog.kopirovatPrilohyZSn', 'Kopirovat prilohy z SN')}
        />
        <RingilInputSwitch
          value={prefill.sn.markAsPrivate}
          disabled={!prefill.sn.copySnAttachments}
          onChange={v => setSn({ markAsPrivate: v })}
          label={tr('DuplicateExpeditionDialog.markAsPrivate', 'Mark copied attachments as private')}
        />

        <HorizontalLine variant={'tertiary'} />
        <COHeadline title={tr('ModuleSettingsExpedition.folders', 'Folders')} variant={'h3'} />
        <RingilInputSwitch
          value={folderSettings.required}
          onChange={v => setFolders2({ required: v })}
          label={tr('ModuleSettingsExpedition.foldersRequired', 'Folder is required when creating a shipment')}
        />
        <RingilInputSwitch
          value={folderSettings.restrictVisibility}
          onChange={v => setFolders2({ restrictVisibility: v })}
          label={tr('ModuleSettingsExpedition.foldersRestrictVisibility', 'Users only see shipments in folders they have access to')}
        />
        <div className={'flex flex-col gap-1'}>
          <span className={'text-sm text-content-secondary'}>
            {tr('ModuleSettingsExpedition.foldersDefaultImportFolder', 'Default folder for the Škoda SAP gateway (public API must send folders itself)')}
          </span>
          <Dropdown
            className="w-full"
            value={folderSettings.defaultImportFolderId ?? null}
            options={folders.map(f => ({ label: f.folderName, value: f.folderId }))}
            onChange={e => setFolders2({ defaultImportFolderId: e.value ?? undefined })}
            showClear={true}
            placeholder={tr('ModuleSettingsExpedition.foldersNoDefault', 'No default folder')}
          />
        </div>

        <RingilButton mode={'filled'} label={tr('generic.save', 'Save')} onClick={() => save()} />
      </div>
    </div>
  );
};

export default ModuleSettingsExpedition;
